/**
 * BillingSuccess — landing after PayPal-hosted approval returns the user.
 *
 * PayPal redirects back with ?subscription_id=I-XXX&ba_token=XXX&token=XXX.
 * We call /billing/sync to pull the latest state from PayPal (the webhook
 * may not have fired yet), then poll /billing/status until it's
 * active/trialing, then redirect to /billing.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import Layout from '../components/Layout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BillingSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // PayPal returns ?subscription_id=I-XXX (plus ba_token, token).
  // Fall back to old `session_id` param for graceful migration.
  const subscriptionId = searchParams.get('subscription_id') || searchParams.get('session_id');
  const [status, setStatus] = useState('polling');

  useEffect(() => {
    let attempts = 0;
    let timer = null;

    const trySync = async () => {
      if (!subscriptionId) return;
      try {
        await axios.post(`${API}/billing/sync`, { subscription_id: subscriptionId }, { withCredentials: true });
      } catch {
        // ignore — webhook may still finalize state
      }
    };

    const poll = async () => {
      attempts += 1;
      // First attempt: actively sync from PayPal before polling
      if (attempts === 1) await trySync();
      try {
        const { data } = await axios.get(`${API}/billing/status`, { withCredentials: true });
        if (['active', 'trialing'].includes(data?.subscription_status)) {
          setStatus('ok');
          setTimeout(() => navigate('/billing'), 1500);
          return;
        }
      } catch {
        // ignore
      }
      if (attempts < 12) {
        timer = setTimeout(poll, 2000);
      } else {
        setStatus('timeout');
      }
    };
    poll();
    return () => timer && clearTimeout(timer);
  }, [navigate, subscriptionId]);

  return (
    <Layout>
      <div className="max-w-lg mx-auto py-20 text-center" data-testid="billing-success">
        {status === 'polling' && (
          <>
            <Loader2 className="w-10 h-10 animate-spin text-[#002FA7] mx-auto mb-4" />
            <h1 className="text-2xl font-heading font-semibold">Activation en cours…</h1>
            <p className="text-neutral-500 mt-2">On synchronise votre abonnement avec PayPal (quelques secondes).</p>
          </>
        )}
        {status === 'ok' && (
          <>
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
            <h1 className="text-2xl font-heading font-semibold">Abonnement activé !</h1>
            <p className="text-neutral-500 mt-2">Bienvenue sur AEGIS Pro 🚀 Redirection vers votre espace…</p>
          </>
        )}
        {status === 'timeout' && (
          <>
            <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-4" />
            <h1 className="text-2xl font-heading font-semibold">Activation un peu lente</h1>
            <p className="text-neutral-500 mt-2">
              Le paiement a été reçu mais la synchronisation prend plus de temps que prévu.
              Rafraîchissez la page d'ici une minute — votre abonnement sera là.
            </p>
            <div className="mt-4 flex gap-2 justify-center">
              <Button variant="outline" onClick={() => window.location.reload()}>Rafraîchir</Button>
              <Button onClick={() => navigate('/billing')}>Voir mon abonnement</Button>
            </div>
            {subscriptionId && <p className="text-[11px] text-neutral-400 mt-6 font-mono">Réf : {subscriptionId}</p>}
          </>
        )}
      </div>
    </Layout>
  );
}
